'use client';

import { useState } from 'react';
import type { SubscriptionTier } from '@prisma/client';
import { Button, type ButtonProps } from '@/components/ui/button';
import type { BillingInterval } from '@/lib/billing-plans';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import type { CheckoutOffer } from '@/lib/recovery-offers';

type StripePricingTier = SubscriptionTier;

interface SessionWithUserId {
  user: {
    email?: string | null;
    id: string;
    image?: string | null;
    name?: string | null;
  };
}

interface SubscriptionButtonProps {
  chatId?: string;
  checkoutOffer?: CheckoutOffer;
  checkoutSource?: string;
  messageId?: string;
  messagesRemaining?: number;
  tier: StripePricingTier;
  label?: string;
  className?: string;
  size?: ButtonProps['size'];
  variant?: ButtonProps['variant'];
  cancelPath?: string;
  successPath?: string;
  interval?: BillingInterval;
  disabled?: boolean;
}

export function SubscriptionButton({
  chatId,
  checkoutOffer,
  checkoutSource,
  messageId,
  messagesRemaining,
  tier,
  label,
  className,
  size = 'lg',
  variant,
  cancelPath,
  successPath = '/success',
  interval = 'month',
  disabled = false,
}: SubscriptionButtonProps) {
  const { data: sessionData, status } = useSession();
  const session = sessionData as SessionWithUserId | null;
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [showAuthDialog, setShowAuthDialog] = useState(false);

  const handleSubscribe = async () => {
    if (status === 'loading') return;

    if (!session || !session.user) {
      setShowAuthDialog(true);
      return;
    }

    setIsLoading(true);

    try {
      const success = new URL('/success', window.location.origin);
      if (successPath !== '/success') success.searchParams.set('returnTo', successPath);
      const successUrl = success.toString();
      const cancelUrl = cancelPath ? new URL(cancelPath, window.location.origin).toString() : window.location.href;
      const requestBody = {
        chatId,
        checkoutOffer,
        checkoutSource,
        messageId,
        messagesRemaining,
        tier,
        interval,
        successUrl,
        cancelUrl,
      };
      const response = await fetch('/api/stripe/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Checkout error:', errorData);
        throw new Error(errorData.details || errorData.error || 'Failed to create checkout session');
      }

      const { url } = await response.json();

      if (!url || !url.startsWith('https://checkout.stripe.com/')) {
        throw new Error('Checkout could not be opened. Please try again.');
      }

      window.location.assign(url);
    } catch (error) {
      console.error('Subscription error:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'An error occurred while processing your subscription',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAuthRedirect = () => {
    router.push('/auth/signin');
  };

  return (
    <>
      <Button
        className={cn(
          'w-full transition-transform',
          !variant && !className && 'bg-white text-blue-600 hover:scale-105 hover:bg-white',
          className
        )}
        size={size}
        variant={variant}
        onClick={handleSubscribe}
        disabled={disabled || isLoading || status === 'loading'}>
        {isLoading ? 'Processing...' : (label ?? `Subscribe to ${tier}`)}
      </Button>

      <Dialog open={showAuthDialog} onOpenChange={setShowAuthDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Authentication Required</DialogTitle>
            <DialogDescription>Please sign in to continue with your subscription.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={() => setShowAuthDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAuthRedirect}>Sign In</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
